import {
  Component,
  input,
  output,
  signal,
  inject,
  computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { FiltrosProducto, Producto } from 'src/app/domain/model/producto/producto';
import { ProductoRepository } from 'src/app/domain/service/producto/producto-repository';
import {
  sanitizeText,
  sanitizeTextPreserveSpaces
} from 'src/app/common/input-utils';
import {
  DEFAULT_ROWS_PER_PAGE,
  PAGE_SIZE_OPTIONS
} from 'src/app/common/constants-ejb';
import { busquedaProductoPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';
import { resolveSearchErrorMessage } from 'src/app/presentation/shared/busquedas/base-busqueda-paginada-dialog';
import { BusquedaPaginadorComponent } from 'src/app/presentation/shared/busquedas/busqueda-paginador.component';

/**
 * Resultado emitido al seleccionar un producto.
 */
export interface ProductoSeleccionado {
  cdproducto: string;
  dsproducto: string;
}

/**
 * Diálogo de búsqueda de Productos.
 *
 * Replica el legacy GWT: BuscadorProductosActivity / SP_PSC_GET_PRODUCTOS.
 *
 * Filtros:
 * - Código de producto
 * - Descripción de producto
 *
 * Contexto opcional:
 * - cdtarea: filtra productos relacionados a la tarea (T094_TAREAS_ACTIVIDAD)
 * - cdclasificacion: sub-filtra por clasificación
 *
 * @author Sura - Migración GWT a Angular
 */
@Component({
  selector: 'app-busqueda-producto-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    ProgressSpinnerModule,
    TooltipModule,
    BusquedaPaginadorComponent
  ],
  styleUrls: ['../../shared/styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      header="Búsqueda de Producto"
      [(visible)]="visible"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '44rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      [closable]="true"
      (onHide)="onCancelar()"
    >
      <!-- Filtros -->
      <div
        class="border rounded p-4 mb-4 bg-gray-50 dark:bg-gray-800 dark:border-gray-700"
      >
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
              >Código</label
            >
            <input
              pInputText
              [(ngModel)]="filtroCodigo"
              (input)="filtroCodigo = sanitizeTextPreserveSpaces(filtroCodigo)"
              class="w-full"
              maxlength="30"
              placeholder="Ej: P001"
              (keyup.enter)="buscar()"
            />
          </div>
          <div>
            <label class="block text-sm text-gray-600 dark:text-gray-300 mb-1"
              >Descripción</label
            >
            <input
              pInputText
              [(ngModel)]="filtroDescripcion"
              (input)="filtroDescripcion = sanitizeTextPreserveSpaces(filtroDescripcion)"
              class="w-full"
              maxlength="100"
              placeholder="Ej: Higiene Industrial"
              (keyup.enter)="buscar()"
            />
          </div>
        </div>
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 mt-4 w-full"
        >
          <button
            pButton
            icon="pi pi-search"
            label="Buscar"
            class="p-button-sm min-h-[44px]"
            (click)="buscar()"
            [disabled]="loading()"
          ></button>
          <button
            pButton
            icon="pi pi-eraser"
            label="Limpiar"
            class="p-button-sm p-button-secondary p-button-outlined min-h-[44px]"
            (click)="limpiarFiltros()"
          ></button>
        </div>
      </div>

      <!-- Mensaje -->
      @if (mensaje()) {
        <div class="p-3 mb-3 rounded bg-yellow-100 text-yellow-800 text-sm">
          <i class="pi pi-exclamation-triangle mr-2"></i>{{ mensaje() }}
        </div>
      }

      <!-- Loading -->
      @if (loading()) {
        <div class="flex justify-center p-4">
          <p-progressSpinner
            strokeWidth="4"
            [style]="{ width: '50px', height: '50px' }"
          ></p-progressSpinner>
        </div>
      }

      <!-- Tabla -->
      @if (!loading() && items().length > 0) {
        <div
          class="font-medium text-gray-700 dark:text-gray-200 p-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm flex justify-between items-center"
        >
          <span>Listado de Productos</span>
          @if (totalRegistros() > 0) {
            <span class="text-xs text-gray-500 dark:text-gray-400">
              Registros del {{ registroInicio() }} hasta el {{ registroFin() }} de
              {{ totalRegistros() }}
            </span>
          }
        </div>
        <div class="md:hidden space-y-3 p-3">
          @for (item of items(); track item.cdproducto) {
            <div
              class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
            >
              <div class="mb-3 min-w-0">
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  Descripción
                </div>
                <div
                  class="font-semibold text-gray-800 dark:text-gray-100 truncate"
                  [attr.title]="tituloTabla(item.dsproducto)"
                >
                  {{ textoTabla(item.dsproducto) }}
                </div>
              </div>
              <div class="grid grid-cols-1 gap-2 text-sm mb-3">
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Código:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(item.cdproducto)
                  }}</span>
                </div>
              </div>
              <p-button
                label="Seleccionar"
                icon="pi pi-check"
                size="small"
                styleClass="w-full min-h-[44px]"
                (onClick)="seleccionar(item)"
              />
            </div>
          }
        </div>

        <div class="hidden md:block">
          <div
            role="region"
            [attr.aria-label]="tableConfig().a11y.tableAriaLabel"
            class="sr-only"
          ></div>
          <p-table
            [resizableColumns]="true"
            columnResizeMode="expand"
            [value]="items()"
            [scrollable]="true"
            [responsiveLayout]="'scroll'"
            [tableStyle]="{
              'min-width': tableConfig().responsive.minWidth || 'max-content'
            }"
            [scrollHeight]="tableConfig().responsive.scrollHeight || '300px'"
            [selectionMode]="tableSelectionMode()"
            [(selection)]="seleccionado"
            [styleClass]="tableStyleClass()"
            (onRowSelect)="onRowSelect($event)"
          >
            <ng-template pTemplate="header">
              <tr>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Código">Código</span>
                </th>
                <th pResizableColumn>
                  <span class="grid-header-text" title="Descripción"
                    >Descripción</span
                  >
                </th>
                <th pResizableColumn class="text-center">
                  <span class="grid-header-text" title="Seleccionar"
                    >Seleccionar</span
                  >
                </th>
              </tr>
            </ng-template>

            <ng-template pTemplate="body" let-item>
              <tr [pSelectableRow]="item" class="cursor-pointer">
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(item.cdproducto)"
                    >{{ textoTabla(item.cdproducto) }}</span
                  >
                </td>
                <td>
                  <span
                    class="grid-cell-text"
                    [attr.title]="tituloTabla(item.dsproducto)"
                    >{{ textoTabla(item.dsproducto) }}</span
                  >
                </td>
                <td class="text-center">
                  <button
                    pButton
                    label="Seleccionar"
                    class="p-button-sm p-button-text"
                    (click)="seleccionar(item)"
                  ></button>
                </td>
              </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="3" class="text-center text-gray-500 py-4">
                  No se encontraron productos.
                </td>
              </tr>
            </ng-template>
          </p-table>
        </div>

        <!-- Paginación -->
        <app-busqueda-paginador
          [currentPage]="paginaActual()"
          [totalPages]="totalPages()"
          [hasPrevious]="hasPrevious()"
          [hasNext]="hasNext()"
          [pageSizeOptions]="pageSizeOptions"
          [selectedPageSize]="pageSize()"
          (pageSizeChange)="onPageSizeChange($event)"
          (first)="irPrimeraPagina()"
          (previous)="paginaAnterior()"
          (next)="paginaSiguiente()"
          (last)="irUltimaPagina()"
        ></app-busqueda-paginador>
      }

      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <button
            pButton
            label="Cerrar"
            icon="pi pi-times"
            class="p-button-outlined p-button-secondary p-button-sm min-h-[44px]"
            (click)="onCancelar()"
          ></button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class BusquedaProductoDialogComponent {
  private readonly productoRepo = inject(ProductoRepository);

  // === Inputs ===
  /**
   * Visibilidad del diálogo.
   * Sigue el mismo patrón que otros busqueda-*-dialog:
   * se muestra con @if(visible) desde el padre y se cierra emitiendo onClose.
   */
  visible = true;

  /**
   * Código de tarea para filtrar productos relacionados.
   * Cuando está presente, busca en T094_TAREAS_ACTIVIDAD (como el SP).
   * Sin él, busca en TPSC_ORDEN_SERVICIO_DETAL (histórico).
   */
  cdtarea = input<string | undefined>(undefined);
  cdclasificacion = input<string | undefined>(undefined);

  /** Emitido al seleccionar un producto */
  onSelect = output<ProductoSeleccionado>();
  onClose = output<void>();

  // === Estado ===
  loading = signal(false);
  items = signal<Producto[]>([]);
  busquedaRealizada = signal(false);

  // Paginación por offset (page/size): el buscador de productos lo pagina el SP del legacy
  // SP_PSC_GET_PRODUCTOS (inudesde/inuhasta), por eso no es keyset (acceso-datos-oracle §7,
  // excepción de SP). El total es exacto (GET /productos/count).
  totalRegistros = signal(0);
  paginaActual = signal(1);
  pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  readonly totalPages = computed(() => {
    const size = this.pageSize();
    const total = this.totalRegistros();
    return size > 0 && total > 0 ? Math.ceil(total / size) : 0;
  });

  /**
   * Con offset y total exacto, Anterior/Siguiente se derivan de la página y del total
   * (skill frontend-girop §17.5). En listados keyset con servidor se usan los flags de la
   * respuesta (§17.7); este buscador no es keyset.
   */
  readonly hasPrevious = computed(() => this.paginaActual() > 1);
  readonly hasNext = computed(() => this.paginaActual() < this.totalPages());

  readonly registroInicio = computed(() => {
    if (this.totalRegistros() === 0) return 0;
    return (this.paginaActual() - 1) * this.pageSize() + 1;
  });

  readonly registroFin = computed(() =>
    Math.min(this.paginaActual() * this.pageSize(), this.totalRegistros())
  );

  readonly tableConfig = computed(() =>
    busquedaProductoPreset.createConfig({
      selection: {
        mode: 'single',
        selected: this.seleccionado as unknown as Record<string, unknown> | null
      },
      style: {
        tableClassName: 'tabla-busqueda-producto',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.loading(),
        emptyMessage: 'No se encontraron productos'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: 'max-content',
        scrollHeight: '300px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de busqueda de productos',
        announceSelection: true,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly tableStyleClass = computed(() => {
    const config = this.tableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  readonly tableSelectionMode = computed<'single' | 'multiple' | undefined>(
    () => {
      const mode = this.tableConfig().selection.mode;
      if (mode === 'single' || mode === 'multiple') {
        return mode;
      }
      return undefined;
    }
  );

  mensaje = signal<string | null>(null);
  seleccionado: Producto | null = null;

  filtroCodigo = '';
  filtroDescripcion = '';

  /** Sanitización XSS en tiempo real (seguridad-girop §3); conserva espacios. */
  readonly sanitizeTextPreserveSpaces = sanitizeTextPreserveSpaces;

  // === Busqueda ===
  async buscar(): Promise<void> {
    this.mensaje.set(null);
    await this.cargarPagina(1);
  }

  private buildFiltros(): FiltrosProducto {
    return {
      // sanitizeText también recorta (antes .trim()); seguridad-girop §3.
      cdproducto: sanitizeText(this.filtroCodigo) || undefined,
      dsproducto: sanitizeText(this.filtroDescripcion) || undefined,
      cdtarea: this.cdtarea(),
      cdclasificacion: this.cdclasificacion()
    };
  }

  /**
   * Carga la página `pagina` (1-based). La página visible solo cambia con la respuesta
   * exitosa y se ignora una petición mientras otra está en curso.
   */
  private async cargarPagina(pagina: number): Promise<void> {
    if (this.loading()) {
      return;
    }
    this.loading.set(true);

    try {
      const filtros = this.buildFiltros();
      const [productos, total] = await Promise.all([
        this.productoRepo.buscarProductos({
          ...filtros,
          page: pagina - 1,
          size: this.pageSize()
        }),
        this.productoRepo.contarProductos(filtros)
      ]);

      this.items.set(productos);
      this.totalRegistros.set(total);
      this.paginaActual.set(pagina);
      this.busquedaRealizada.set(true);

      if (productos.length === 0) {
        this.mensaje.set(
          'No se encontraron productos con los filtros ingresados.'
        );
      }
    } catch (error) {
      this.mensaje.set(
        resolveSearchErrorMessage(
          error,
          'Error al buscar productos. Intente nuevamente.'
        )
      );
      this.items.set([]);
      this.totalRegistros.set(0);
      this.paginaActual.set(1);
    } finally {
      this.loading.set(false);
    }
  }

  // Navegación por página (offset)
  irPrimeraPagina(): Promise<void> {
    return this.cargarPagina(1);
  }

  async paginaAnterior(): Promise<void> {
    if (this.loading() || !this.hasPrevious()) {
      return;
    }
    await this.cargarPagina(this.paginaActual() - 1);
  }

  async paginaSiguiente(): Promise<void> {
    if (this.loading() || !this.hasNext()) {
      return;
    }
    await this.cargarPagina(this.paginaActual() + 1);
  }

  async irUltimaPagina(): Promise<void> {
    if (this.loading() || this.totalRegistros() === 0) {
      return;
    }
    await this.cargarPagina(this.totalPages());
  }

  onPageSizeChange(newPageSize: number): void {
    const size = Number(newPageSize);
    if (!Number.isFinite(size) || size <= 0 || size === this.pageSize()) {
      return;
    }

    this.pageSize.set(size);
    this.paginaActual.set(1);

    if (this.busquedaRealizada()) {
      void this.cargarPagina(1);
    }
  }

  limpiarFiltros(): void {
    this.filtroCodigo = '';
    this.filtroDescripcion = '';
    this.items.set([]);
    this.totalRegistros.set(0);
    this.mensaje.set(null);
    this.busquedaRealizada.set(false);
    this.paginaActual.set(1);
  }

  seleccionar(producto: Producto): void {
    this.onSelect.emit({
      cdproducto: producto.cdproducto,
      dsproducto: producto.dsproducto
    });
    this.visible = false;
    this.onClose.emit();
    this.limpiarFiltros();
  }

  onRowSelect(event: { data?: unknown }): void {
    const producto = event.data as Producto | undefined;
    if (producto?.cdproducto) {
      this.seleccionar(producto);
    }
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }

  onCancelar(): void {
    this.visible = false;
    this.onClose.emit();
    this.limpiarFiltros();
  }
}
